/**
 * v0.4後半 月次実績一括入力 第2フェーズ I-6b 管理部門向けの確認画面 (D-1028) のAPIクライアント。
 * バックエンド: apps/api/src/routes/attendance-review.ts (実装・レビュー完了済み)。
 * 設計: docs/design/v04-monthly-attendance-bulk-entry-phase2-detail.md §9-0/§9-1
 */
import { apiFetch } from './api';

// =============================================================================
// 保存プリセット (D-1053: テナント共有・作成者のみ編集可)
// =============================================================================
export type AttendanceReviewPreset = {
  preset_id: string;
  label: string;
  filters_json: string | null;
  created_by: string;
  created_at: string;
  row_version: number;
};

export const fetchAttendanceReviewPresets = () =>
  apiFetch<{ presets: AttendanceReviewPreset[] }>('/attendance-review/presets');

export const createAttendanceReviewPreset = (input: { label: string; filters_json: string | null }) =>
  apiFetch<{ preset_id: string }>('/attendance-review/presets', { method: 'POST', body: JSON.stringify(input) });

export const updateAttendanceReviewPreset = (
  id: string, input: { label?: string; filters_json?: string | null; row_version: number },
) => apiFetch<{ preset_id: string }>(`/attendance-review/presets/${id}`, { method: 'PUT', body: JSON.stringify(input) });

export const deleteAttendanceReviewPreset = (id: string) =>
  apiFetch<{ deleted: true }>(`/attendance-review/presets/${id}`, { method: 'DELETE' });

// =============================================================================
// 人別・店舗別の集計 (§5-1 の項目、summarizePerson と1対1)
// =============================================================================
export type AttendanceReviewPersonRow = {
  person_key: string;
  employee_id: string | null;
  external_person_id: string | null;
  employee_number: string | null;
  name: string | null;
  employment_type_id: string | null;
  termination_date: string | null;
  // §9-1 HIGH-1: スコープ外の主所属は id/name とも null になり、home_node_out_of_scope が true になる
  home_node_id: string | null;
  home_node_name: string | null;
  home_node_out_of_scope: boolean;
  determinable: boolean;
  reason: string | null;
  worked_days: number;
  public_holiday_days: number;
  paid_leave_days: number;
  compensatory_off_days: number;
  absence_days: number;
  holiday_work_days: number;
  total_labor_minutes: number;
  non_statutory_overtime_minutes: number;
  statutory_overtime_minutes: number;
  over_monthly_60h_minutes: number;
  night_minutes: number;
  legal_holiday_work_minutes: number;
  late_count: number;
  late_minutes: number;
  early_leave_count: number;
  early_leave_minutes: number;
  outside_home_node_work_days: number;
  minor_days: number;
  min_break_shortfall_days: number;
  has_minor_labor_violation: boolean;
  approval_status: string | null;
  // 週契約(所定労働時間)から対象期間ぶんに換算した参考値。契約未設定の社員はnull。
  scheduled_work_days_contracted: number | null;
  scheduled_monthly_minutes_contracted: number | null;
};

export type AttendanceReviewFilters = {
  start_month: string;
  end_month: string;
  node_ids?: string[];
  employment_type_id?: string | null;
  employment_status?: 'all' | 'active' | 'terminated';
};

const buildReviewQuery = (filters: AttendanceReviewFilters): string => {
  const params = new URLSearchParams();
  params.set('start_month', filters.start_month);
  params.set('end_month', filters.end_month);
  if (filters.node_ids && filters.node_ids.length > 0) params.set('node_ids', filters.node_ids.join(','));
  if (filters.employment_type_id) params.set('employment_type_id', filters.employment_type_id);
  if (filters.employment_status && filters.employment_status !== 'all') params.set('employment_status', filters.employment_status);
  return params.toString();
};

export const fetchAttendanceReviewByPerson = (filters: AttendanceReviewFilters) =>
  apiFetch<{ persons: AttendanceReviewPersonRow[] }>(`/attendance-review/by-person?${buildReviewQuery(filters)}`);

export type AttendanceReviewStoreRow = {
  node_id: string;
  node_name: string;
  parent_node_id: string | null;
  parent_node_name: string | null;
  person_count: number;
  worked_days_total: number;
  total_labor_minutes_total: number;
  persons: AttendanceReviewPersonRow[];
};

export const fetchAttendanceReviewByStore = (filters: AttendanceReviewFilters) =>
  apiFetch<{ stores: AttendanceReviewStoreRow[]; total_person_count: number }>(
    `/attendance-review/by-store?${buildReviewQuery(filters)}`,
  );

// =============================================================================
// 月次明細 (§9-0、I-4b読み取り専用の再利用方針)
// =============================================================================
export type AttendanceReviewDetailRecord = {
  work_record_id: string;
  work_date: string;
  seq: number;
  start_at: string | null;
  end_at: string | null;
  break_minutes: number;
  actual_labor_minutes: number | null;
  home_node_id: string;
  work_node_id: string;
  record_kind: 'worked' | 'absence';
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  reason_code_id: string | null;
  late_minutes: number;
  early_leave_minutes: number;
  planned_time_confirmed: 0 | 1;
  source: string;
  approved_at: string | null;
};

export const fetchAttendanceReviewPersonDetail = (
  personKey: string, period: { start_month: string; end_month: string },
) => apiFetch<{ employee_id: string | null; external_person_id: string | null; work_records: AttendanceReviewDetailRecord[] }>(
  `/attendance-review/person/${encodeURIComponent(personKey)}/detail?start_month=${period.start_month}&end_month=${period.end_month}`,
);
